import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Sheet,
    Table,
    Typography
} from '@mui/joy';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import axiosConfig from '../util/axiosConfig';
import { formatDateTime } from '../util/dateFormat';
import { STUDENT_NOTIFICATION_RECEIVED } from '../util/studentNotificationEvents';
import {
    enablePushNotifications,
    isPushNotificationSupported,
    syncPushTokenIfGranted
} from '../util/pushNotifications';

const tableSheetSx = {
    width: '100%',
    maxWidth: '100%',
    borderRadius: 'sm',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '600px',
    WebkitOverflowScrolling: 'touch',
    border: '1px solid',
    borderColor: 'divider'
};

const notificationsTableSx = {
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'fixed',
    '& tbody tr:nth-of-type(even)': {
        backgroundColor: '#f9f9f9'
    },
    '& tbody tr:hover': {
        backgroundColor: '#fff5ed'
    },
    '& thead': {
        position: 'sticky',
        top: 0,
        zIndex: 10
    },
    '& thead th': {
        backgroundColor: '#eb7a24',
        color: 'white',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '2px solid #e55125',
        whiteSpace: 'nowrap'
    },
    '& td, & th': {
        padding: '12px',
        border: '1px solid',
        borderColor: 'divider',
        verticalAlign: 'top',
        overflow: 'hidden'
    },
    '& th:nth-of-type(1), & td:nth-of-type(1)': { width: 160, minWidth: 160 },
    '& th:nth-of-type(2), & td:nth-of-type(2)': { width: 220, minWidth: 220 },
    '& th:nth-of-type(3), & td:nth-of-type(3)': { width: 360, minWidth: 360 },
    '& th:nth-of-type(4), & td:nth-of-type(4)': { width: 100, minWidth: 100 },
    '& th:nth-of-type(5), & td:nth-of-type(5)': { width: 140, minWidth: 140 }
};

const messageCellTextSx = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
};

const Notifications = () => {
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [filter, setFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [pushStatus, setPushStatus] = useState(null);
    const [enablingPush, setEnablingPush] = useState(false);
    const pageSize = 50;

    const fetchPushStatus = useCallback(async () => {
        try {
            const response = await axiosConfig.get('/students/push-status');
            setPushStatus(response.data);
        } catch (err) {
            console.warn('Could not load push status', err);
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/notifications', {
                params: {
                    limit: 500,
                    unreadOnly: filter === 'unread'
                }
            });
            setNotifications(response.data.notifications || []);
            setUnreadCount(response.data.unreadCount || 0);
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load notifications');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        setLoading(true);
        fetchNotifications();
        fetchPushStatus();
    }, [fetchNotifications, fetchPushStatus]);

    const handleEnableBrowserPush = async () => {
        setEnablingPush(true);
        try {
            const result = Notification.permission === 'granted'
                ? await syncPushTokenIfGranted({ preferServer: true, forceRefresh: true })
                : await enablePushNotifications();

            const ok = result.success || result.synced;
            if (!ok) {
                const reason = result.reason || 'unknown';
                if (reason === 'denied') {
                    setError('Browser blocked notifications. Allow them in site settings, then try again.');
                } else if (reason === 'not_configured') {
                    setError(
                        'Web push is not configured on the server. Ask admin to set FIREBASE_WEB_VAPID_KEY and FIREBASE_WEB_APP_ID.'
                    );
                } else {
                    setError(`Could not register this browser for push (${reason}).`);
                }
            } else {
                await fetchPushStatus();
            }
        } catch (err) {
            setError(err.response?.data?.errorMessage || 'Failed to enable browser notifications');
        } finally {
            setEnablingPush(false);
        }
    };

    useEffect(() => {
        const onReceived = () => {
            fetchNotifications();
        };
        window.addEventListener(STUDENT_NOTIFICATION_RECEIVED, onReceived);
        return () => window.removeEventListener(STUDENT_NOTIFICATION_RECEIVED, onReceived);
    }, [fetchNotifications]);

    const handleMarkRead = async (id) => {
        try {
            setActionLoading(true);
            await axiosConfig.patch(`/students/notifications/${id}/read`);
            await fetchNotifications();
        } catch (err) {
            setError(err.response?.data?.errorMessage || 'Failed to mark as read');
        } finally {
            setActionLoading(false);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            setActionLoading(true);
            await axiosConfig.patch('/students/notifications/read-all');
            await fetchNotifications();
        } catch (err) {
            setError(err.response?.data?.errorMessage || 'Failed to mark all as read');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setActionLoading(true);
            await axiosConfig.delete(`/students/notifications/${id}`);
            await fetchNotifications();
        } catch (err) {
            setError(err.response?.data?.errorMessage || 'Failed to delete notification');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2 }}>
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading notifications...</Typography>
            </Box>
        );
    }

    const totalPages = Math.max(1, Math.ceil(notifications.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const startIndex = (currentPage - 1) * pageSize;
    const pageData = notifications.slice(startIndex, startIndex + pageSize);

    return (
        <Box sx={{ p: 0, m: 0, width: '100%', height: '100%', minWidth: 0 }}>
            <Card variant="outlined">
                <CardContent sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <NotificationsActiveIcon sx={{ color: '#eb7a24', fontSize: '1.5rem' }} />
                            <Typography level="h3" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                                Notifications
                            </Typography>
                            {unreadCount > 0 && (
                                <Chip size="sm" color="warning" variant="solid">
                                    {unreadCount} unread
                                </Chip>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                size="sm"
                                variant={filter === 'all' ? 'solid' : 'outlined'}
                                onClick={() => { setFilter('all'); setPage(1); }}
                                sx={filter === 'all' ? { bgcolor: '#eb7a24', '&:hover': { bgcolor: '#e55125' } } : {}}
                            >
                                All
                            </Button>
                            <Button
                                size="sm"
                                variant={filter === 'unread' ? 'solid' : 'outlined'}
                                onClick={() => { setFilter('unread'); setPage(1); }}
                                sx={filter === 'unread' ? { bgcolor: '#eb7a24', '&:hover': { bgcolor: '#e55125' } } : {}}
                            >
                                Unread
                            </Button>
                            <Button
                                size="sm"
                                variant="outlined"
                                startDecorator={<MarkEmailReadIcon />}
                                disabled={actionLoading || unreadCount === 0}
                                onClick={handleMarkAllRead}
                            >
                                Mark all read
                            </Button>
                        </Box>
                    </Box>

                    {error && (
                        <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {isPushNotificationSupported() && (
                        <>
                            {Notification.permission !== 'granted' && (
                                <Alert
                                    color="warning"
                                    variant="soft"
                                    sx={{ mb: 2 }}
                                    endDecorator={
                                        <Button
                                            size="sm"
                                            loading={enablingPush}
                                            onClick={handleEnableBrowserPush}
                                            sx={{ bgcolor: '#eb7a24', '&:hover': { bgcolor: '#e55125' } }}
                                        >
                                            Enable
                                        </Button>
                                    }
                                >
                                    Browser alerts are off. Enable notifications to get pop-ups and the schoolbell sound when a message arrives.
                                </Alert>
                            )}
                            {Notification.permission === 'granted' && pushStatus?.tokenCount === 0 && (
                                <Alert
                                    color="warning"
                                    variant="soft"
                                    sx={{ mb: 2 }}
                                    endDecorator={
                                        <Button
                                            size="sm"
                                            loading={enablingPush}
                                            onClick={handleEnableBrowserPush}
                                            sx={{ bgcolor: '#eb7a24', '&:hover': { bgcolor: '#e55125' } }}
                                        >
                                            Register device
                                        </Button>
                                    }
                                >
                                    Permission is granted but this browser is not registered for push yet. Click Register device, then send a test notification.
                                </Alert>
                            )}
                        </>
                    )}

                    {notifications.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography level="body-lg" sx={{ color: '#6e7072' }}>
                                No notifications yet
                            </Typography>
                        </Box>
                    ) : (
                        <Sheet variant="outlined" sx={tableSheetSx}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
                                <Typography level="body-sm" sx={{ color: '#6e7072' }}>
                                    Showing {startIndex + 1}-{Math.min(startIndex + pageSize, notifications.length)} of {notifications.length}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Button size="sm" variant="outlined" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                                        Prev
                                    </Button>
                                    <Typography level="body-sm">Page {currentPage} / {totalPages}</Typography>
                                    <Button size="sm" variant="outlined" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                                        Next
                                    </Button>
                                </Box>
                            </Box>
                            <Table aria-label="Notifications table" sx={notificationsTableSx}>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Title</th>
                                        <th>Message</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageData.map((item) => (
                                        <tr key={item.id} style={!item.isRead ? { fontWeight: 500 } : undefined}>
                                            <td>
                                                <Typography level="body-sm" sx={{ whiteSpace: 'nowrap' }}>
                                                    {formatDateTime(item.createdAt)}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-md">{item.title}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm" sx={messageCellTextSx}>
                                                    {item.body || '-'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Chip size="sm" variant="soft" color={item.isRead ? 'neutral' : 'warning'}>
                                                    {item.isRead ? 'Read' : 'Unread'}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {!item.isRead && (
                                                        <IconButton
                                                            size="sm"
                                                            variant="plain"
                                                            color="primary"
                                                            aria-label="Mark as read"
                                                            disabled={actionLoading}
                                                            onClick={() => handleMarkRead(item.id)}
                                                        >
                                                            <MarkEmailReadIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    <IconButton
                                                        size="sm"
                                                        variant="plain"
                                                        color="danger"
                                                        aria-label="Delete notification"
                                                        disabled={actionLoading}
                                                        onClick={() => handleDelete(item.id)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default Notifications;
